import React from 'react';
import { EMPLOYMENT_TYPE_OPTIONS } from '../../employees/utils/constants';
import { Position } from '../services/ContractWizardService';
import css from './ContractWizardModal.module.css';

export interface ContractStepPositionProps {
  selectedWA: number | null;
  setSelectedWA: (wa: number | null) => void;
  selectedEmploymentType: string;
  setSelectedEmploymentType: (type: string) => void;
  selectedPositionKey: string;
  setSelectedPositionKey: (key: string) => void;
  startDate: string;
  setStartDate: (date: string) => void;
  workAreas: Array<{ id: number; name: string }>;
  positions: Position[];
  filteredPositions: Position[];
  loadingPositions: boolean;
  isAnyPosition: boolean;
  onOpenAnnexModal: () => void;
}

export function ContractStepPosition({
  selectedWA,
  setSelectedWA,
  selectedEmploymentType,
  setSelectedEmploymentType,
  selectedPositionKey,
  setSelectedPositionKey,
  startDate,
  setStartDate,
  workAreas,
  positions,
  filteredPositions,
  loadingPositions,
  isAnyPosition,
  onOpenAnnexModal,
}: ContractStepPositionProps) {
  const benefitLabels: Record<string, string> = {
    uniform: 'Uniform',
    meal_allowance: 'Essenszuschuss',
    service_vehicle: 'Dienstwagen',
  };

  const renderBenefits = () => {
    if (!selectedPositionKey || selectedPositionKey === null) return null;

    const selectedPos = positions.find(
      (p) => p.position_key === selectedPositionKey
    );
    const benefits = selectedPos?.default_benefits;

    if (!benefits || Object.keys(benefits).length === 0) return null;

    const activeBenefits = Object.entries(benefits)
      .filter(([_, enabled]) => enabled)
      .map(([key]) => benefitLabels[key] || key);

    if (activeBenefits.length === 0) return null;

    return (
      <div className={css.benefitsBox}>
        <div className={css.benefitsTitle}>
          Benefits für diese Position:
        </div>
        <div className={css.benefitsList}>
          {activeBenefits.map((benefit) => (
            <span key={benefit} className={css.benefitPill}>
              {benefit}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={css.step0Wrapper}>
      <div className={css.step0Header}>
        <h3 className={css.sectionHeading}>
          Arbeitsbereich &amp; Position
        </h3>
        <button
          onClick={onOpenAnnexModal}
          className={css.createOtherBtn}
        >
          Create other...
        </button>
      </div>

      <div>
        <label className={css.labelStyle}>Work Area *</label>
        <select
          value={selectedWA || ''}
          onChange={(e) => setSelectedWA(Number(e.target.value))}
          className={css.inputStyle}
        >
          <option value="">Select...</option>
          {workAreas.map((wa) => (
            <option key={wa.id} value={wa.id}>
              {wa.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={css.labelStyle}>Employment Type *</label>
        <select
          value={selectedEmploymentType}
          onChange={(e) => setSelectedEmploymentType(e.target.value)}
          className={css.inputStyle}
        >
          <option value="">Select...</option>
          {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={css.labelStyle}>Position *</label>
        {loadingPositions ? (
          <div className={css.loadingPositions}>
            Loading positions...
          </div>
        ) : (
          <select
            value={selectedPositionKey}
            onChange={(e) => setSelectedPositionKey(e.target.value)}
            disabled={!selectedWA || filteredPositions.length === 0}
            className={css.inputStyle}
          >
            <option value="">Select...</option>
            {filteredPositions.map((pos) => (
              <option key={pos.position_key} value={pos.position_key}>
                {pos.label}
              </option>
            ))}
          </select>
        )}

        {renderBenefits()}
      </div>

      {isAnyPosition && (
        <div className={css.anyPositionInfo}>
          {'💡 '}
          <strong>Schnelltest-Modus:</strong> Sie haben &quot;Allgemein
          (ohne Position)&quot; gewählt. Das Template-Matching erfolgt
          über ANY-Binding (position_key = null) basierend auf
          Employment Type. Sie können im nächsten Schritt eine freie
          Positionsbezeichnung eingeben.
        </div>
      )}

      <div>
        <label className={css.labelStyle}>Start Date *</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className={css.inputStyle}
        />
      </div>
    </div>
  );
}
